import { useEffect, useRef } from "react";

import { BRAND } from "../brand/warmth";

/**
 * A burst of confetti for the moment someone finds the spot.
 *
 * Hand-rolled rather than a dependency: it is 60 lines, it uses the brand
 * palette directly, and it lets the pieces be pin-shaped (three round corners,
 * one sharp) so even the celebration is the logo.
 */
const COLORS = [BRAND.flare, BRAND.beacon, BRAND.chill, BRAND.moss, BRAND.ember];

type Piece = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vr: number;
  size: number;
  color: string;
};

export function Confetti({ fire }: { fire: boolean }) {
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!fire) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const el = canvas.current;
    if (!el) return;
    const ctx = el.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = (el.width = window.innerWidth * dpr);
    const h = (el.height = window.innerHeight * dpr);
    el.style.width = "100%";
    el.style.height = "100%";

    // Two side cannons, angled inward — reads as celebration rather than rain.
    const pieces: Piece[] = [];
    for (const side of [0, 1]) {
      for (let i = 0; i < 70; i++) {
        const spread = (Math.random() - 0.5) * 0.9;
        const power = 14 + Math.random() * 16;
        pieces.push({
          x: side === 0 ? 0 : w,
          y: h * 0.72,
          vx: (side === 0 ? 1 : -1) * power * (0.7 + Math.random() * 0.6) * dpr,
          vy: (-power * (0.9 + Math.random() * 0.7) + spread * 8) * dpr,
          rot: Math.random() * Math.PI * 2,
          vr: (Math.random() - 0.5) * 0.35,
          size: (7 + Math.random() * 8) * dpr,
          color: COLORS[(Math.random() * COLORS.length) | 0],
        });
      }
    }

    let raf = 0;
    let frame = 0;
    const tick = () => {
      frame++;
      ctx.clearRect(0, 0, w, h);
      for (const p of pieces) {
        p.vy += 0.55 * dpr; // gravity
        p.vx *= 0.992; // drag
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        const r = p.size * 0.34;
        ctx.beginPath();
        // Pin silhouette: three rounded corners and one sharp point.
        ctx.moveTo(-p.size / 2, -p.size / 2 + r);
        ctx.arcTo(-p.size / 2, -p.size / 2, -p.size / 2 + r, -p.size / 2, r);
        ctx.lineTo(p.size / 2 - r, -p.size / 2);
        ctx.arcTo(p.size / 2, -p.size / 2, p.size / 2, -p.size / 2 + r, r);
        ctx.lineTo(p.size / 2, p.size / 2 - r);
        ctx.arcTo(p.size / 2, p.size / 2, p.size / 2 - r, p.size / 2, r);
        ctx.lineTo(-p.size / 2, p.size / 2);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
      if (frame < 260) raf = requestAnimationFrame(tick);
      else ctx.clearRect(0, 0, w, h);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [fire]);

  if (!fire) return null;
  return (
    <canvas
      ref={canvas}
      aria-hidden
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 200,
      }}
    />
  );
}

/** A short buzz on a phone. Silently absent on desktop. */
export function haptic(pattern: number | number[] = 12) {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* unsupported */
  }
}
